import React, {useState, useRef, useCallback, useMemo, useEffect} from 'react';
import styles from './styles.module.scss';
import classNames from 'classnames';
import {TextMedium16} from "@/app/components/texts";
import useClickOutside from '@/app/hooks/useClickOutside';
import {
  getDaysInMonth,
  getMonthNames,
  getYearsArray,
  getDaysArray,
  filterDaysForPast,
  filterMonthsForPast,
  filterYearsForPast
} from '@/app/utils/dateUtils';
import DatePickerPanel from './DatePickerPanel';

interface DatePickerProps {
  initialDate?: Date;
  onChange?: (date: Date) => void;
  className?: string
  isWhite?: boolean,
  onlyDayAndMonth?: boolean
  isReverse?: boolean
  disablePast?: boolean
}

const MONTHS = getMonthNames();
const YEARS = getYearsArray(100);

type PanelType = 'month' | 'day' | 'year' | null;

const DatePicker: React.FC<DatePickerProps> = ({
                                                 initialDate = new Date(),
                                                 onChange,
                                                 className,
                                                 isWhite = false,
                                                 onlyDayAndMonth = false,
                                                 isReverse = false,
                                                 disablePast = true
                                               }) => {

  const [selected, setSelected] = useState(() => {
    const d = new Date(initialDate);

    if (disablePast) {
      const now = new Date();
      if (d < now) {
        return now;
      }
    }
    return d;
  });
  const [openType, setOpenType] = useState<PanelType>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const handleClickOutside = useCallback(() => {
    setOpenType(null);
  }, []);

  useClickOutside(wrapperRef, handleClickOutside);

  // Update selected state when initialDate changes
  useEffect(() => {
    const d = new Date(initialDate);

    if (disablePast) {
      const now = new Date();
      if (d < now) {
        return;
      }
    }

    setSelected(d);
  }, [initialDate, disablePast]);

  function update(date: Date) {
    const now = new Date();
    // Compare only year, month, and day components, ignoring time
    if (disablePast &&
      (date.getFullYear() < now.getFullYear() ||
        (date.getFullYear() === now.getFullYear() && date.getMonth() < now.getMonth()) ||
        (date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth() && date.getDate() < now.getDate()))) {
      // Instead of rejecting the selection, set the date to the current date (now)
      setSelected(new Date(now));
      onChange?.(new Date(now));
      setOpenType(null);
      return;
    }
    setSelected(new Date(date));
    onChange?.(new Date(date));
    setOpenType(null);
  }

  const selectedYear = selected.getFullYear();
  const selectedMonth = selected.getMonth();

  // Get days array for the selected month and year
  const DAYS = getDaysArray(selectedMonth, selectedYear);

  // Filter days, months, and years based on disablePast setting
  const filteredDays = filterDaysForPast(DAYS, selectedYear, selectedMonth, disablePast);

  // Create months array with names and indices
  const monthsWithIndices = useMemo(() =>
      MONTHS.map((m, i) => ({m, i})),
    []);

  // Filter months based on disablePast setting
  const filteredMonths = filterMonthsForPast(monthsWithIndices, selectedYear, disablePast);

  // Filter years based on disablePast setting
  const filteredYears = filterYearsForPast(YEARS, disablePast);

  return (
    <div className={classNames(styles.datepicker, className)} ref={wrapperRef}>
      <div className={classNames(styles.input, {[styles.white]: isWhite}, {[styles.reverse]: isReverse})}>
        <div onClick={() => setOpenType(openType === 'month' ? null : 'month')} className={styles.item}>
          <TextMedium16 text={MONTHS[selected.getMonth()]}/>
        </div>
        <div onClick={() => setOpenType(openType === 'day' ? null : 'day')} className={styles.item}>
          <TextMedium16 text={selected.getDate().toString()}/>
        </div>
        {!onlyDayAndMonth &&
            <div onClick={() => setOpenType(openType === 'year' ? null : 'year')} className={styles.item}>
                <TextMedium16 text={selected.getFullYear().toString()}/>
            </div>}
      </div>
      <DatePickerPanel
        openType={openType}
        isWhite={isWhite}
        selected={selected}
        filteredDays={filteredDays}
        filteredMonths={filteredMonths}
        filteredYears={filteredYears}
        onUpdate={update}
      />
    </div>
  );
};

export default DatePicker;